import { 
  Flex, 
  Text, 
  Switch, 
  useColorModeValue, 
  useToast,
  VStack,
  HStack,
  Icon,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
} from "@chakra-ui/react";
import Card from "../../../../components/card/Card";
import { useState, useEffect } from "react";
import { 
  getNotificationPreferences, 
  updateNotificationPreferences 
} from "../../../../services/notificationService";
import { 
  MdShoppingCart, 
  MdPerson, 
  MdInventory, 
  MdStar, 
  MdPayment,
  MdCancel,
  MdCheckCircle,
  MdWarning,
  MdCampaign,
  MdPeople,
  MdError,
  MdSchedule,
  MdAllInbox,
} from "react-icons/md";

export default function Notifications(props) {
  const { ...rest } = props;
  const toast = useToast(); 
  const [switchStates, setSwitchStates] = useState({
    orderNotification: true,
    orderCancelled: true,
    paymentFailed: true,
    waiterCall: true,
    cashRequest: true,
    reviewNotification: false,
    stockUpdate: false,
    lowStockAlert: true,
    campaignUsed: false,
    campaignExpiring: false,
    systemUpdate: true,
    employeeTasksDueDate: false,
  });

  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("gray.500", "gray.400");
  const brandColor = useColorModeValue("#364fab", "#364fab");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        setLoading(true);
        const response = await getNotificationPreferences();
        if (response.success && response.data) {
          setSwitchStates((prev) => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        console.error('Bildirim tercihleri yüklenemedi:', error);
        toast({
          title: "Hata",
          description: "Bildirim tercihleri yüklenirken bir hata oluştu",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, []);

  const handleSwitchChange = async (key, label) => {
    const newValue = !switchStates[key];
    
    setSwitchStates((prevState) => ({
      ...prevState,
      [key]: newValue,
    }));

    try {
      await updateNotificationPreferences({
        [key]: newValue
      });

      toast({
        title: newValue ? "Bildirim etkinleştirildi" : "Bildirim devre dışı bırakıldı",
        description: `${label} bildirimi ${newValue ? 'etkinleştirildi' : 'devre dışı bırakıldı'}.`,
        status: newValue ? "success" : "warning",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      setSwitchStates((prevState) => ({
        ...prevState,
        [key]: !newValue,
      }));

      toast({
        title: "Hata",
        description: "Bildirim tercihi güncellenirken bir hata oluştu",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const notificationCategories = [
    {
      title: "Sipariş Bildirimleri",
      icon: MdShoppingCart,
      notifications: [
        { key: "orderNotification", label: "Yeni sipariş", icon: MdShoppingCart },
        { key: "orderCancelled", label: "Sipariş iptal edildi", icon: MdCancel },
        { key: "paymentFailed", label: "Ödeme başarısız", icon: MdError },
      ]
    },
    {
      title: "Müşteri Bildirimleri",
      icon: MdPeople,
      notifications: [
        { key: "waiterCall", label: "Garson çağırma", icon: MdPerson },
        { key: "cashRequest", label: "Nakit ödeme isteği", icon: MdPayment },
        { key: "reviewNotification", label: "Yorum ve değerlendirme", icon: MdStar },
      ]
    },
    {
      title: "Stok Bildirimleri",
      icon: MdInventory,
      notifications: [
        { key: "stockUpdate", label: "Stok güncelleme", icon: MdInventory },
        { key: "lowStockAlert", label: "Kritik stok uyarısı", icon: MdWarning },
      ]
    },
    {
      title: "Kampanya Bildirimleri",
      icon: MdCampaign,
      notifications: [
        { key: "campaignUsed", label: "Kampanya kullanıldı", icon: MdCampaign },
        { key: "campaignExpiring", label: "Kampanya süresi dolmak üzere", icon: MdSchedule },
      ]
    },
    {
      title: "Genel",
      icon: MdAllInbox,
      notifications: [
        { key: "systemUpdate", label: "Sistem güncellemeleri", icon: MdCheckCircle },
        { key: "employeeTasksDueDate", label: "Çalışan görevleri bitiş tarihi", icon: MdPeople },
      ]
    },
  ];

  return (
    <Card mb="20px" mx="auto" maxW="100%" {...rest}>
      <Flex align="center" w="100%" justify="space-between" mb="30px">
        <Heading color={textColorPrimary} fontWeight="bold" fontSize="xl" mb="4px">
          Bildirim Ayarları
        </Heading>
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" py={12} minH="200px">
          <Spinner size="lg" thickness="4px" color={brandColor} />
        </Flex>
      ) : (
      <Tabs colorScheme="brand" variant="enclosed" defaultIndex={0}>
        <TabList flexWrap="wrap" mb={4}>
          {notificationCategories.map((category, index) => (
            <Tab key={index} fontSize="sm" fontWeight="500">
              <HStack spacing={2}>
                <Icon as={category.icon} w={4} h={4} />
                <Text display={{ base: "none", md: "block" }}>
                  {category.title.replace(" Bildirimleri", "")}
                </Text>
              </HStack>
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {notificationCategories.map((category, categoryIndex) => (
            <TabPanel key={categoryIndex} px={0}>
              <VStack spacing={2} align="stretch">
                {category.notifications.map((notification) => (
                  <Flex 
                    key={notification.key}
                    align="center" 
                    justify="space-between" 
                    p={3}
                    borderRadius="8px"
                    _hover={{ bg: "#364fab10" }}
                    transition="all 0.2s"
                  >
                    <HStack spacing={3}>
                      <Icon 
                        as={notification.icon} 
                        color={switchStates[notification.key] ? "#364fab" : textColorSecondary} 
                        w={5} 
                        h={5} 
                      />
                      <Text color={textColorPrimary} fontSize="sm" fontWeight="500">
                        {notification.label}
                      </Text>
                    </HStack>
                    <Switch
                      isChecked={switchStates[notification.key]}
                      color={brandColor}
                      onChange={() => handleSwitchChange(notification.key, notification.label)}
                    />
                  </Flex>
                ))}
              </VStack>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
      )}
    </Card>
  );
}
